




import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from "react-router-dom";
import { jwtDecode } from 'jwt-decode'; 
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';

import {
  TextField, Button, Typography, Container, Paper, 
  InputAdornment, IconButton, Dialog, DialogTitle, 
  DialogContent, DialogActions, CircularProgress, 
  Box, Stack, styled, Fade, Divider
} from '@mui/material';
import {
  Visibility, VisibilityOff, PersonOutline, 
  PhoneIphone, EmailOutlined, LockOutlined,
  AppRegistration
} from '@mui/icons-material';

/* ─── Design Tokens ─── */
const T = {
  amber:   '#C2823C',
  amberLight: '#E8A85A',
  cream:   '#FAF8F4',
  ivory:   '#FFFEFB',
  ink:     '#1a1510',
  inkMid:  '#4a3f30',
  inkLight:'#8a7055',
  border:  'rgba(194,130,60,0.18)',
  shadow:  '0 20px 60px rgba(100,70,30,0.12)',
};

/* ─── Styled Components ─── */
const PageWrapper = styled(Box)(() => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: T.cream,
  padding: '40px 20px',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    backgroundImage: `radial-gradient(circle at 10% 10%, rgba(194,130,60,0.05) 0%, transparent 30%),
                      radial-gradient(circle at 90% 90%, rgba(194,130,60,0.05) 0%, transparent 30%)`,
    pointerEvents: 'none',
  }
}));

const RegisterCard = styled(Paper)(({ theme }) => ({
  background: T.ivory,
  border: `1px solid ${T.border}`,
  borderRadius: '32px',
  padding: theme.spacing(6, 4),
  width: '100%',
  maxWidth: '500px',
  boxShadow: T.shadow,
  zIndex: 1,
}));

const PrimaryButton = styled(Button)(() => ({
  borderRadius: '50px',
  padding: '12px 0',
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '1rem',
  background: `linear-gradient(135deg, ${T.amber} 0%, ${T.amberLight} 100%)`,
  color: '#fff',
  boxShadow: '0 8px 20px rgba(194,130,60,0.25)',
  '&:hover': {
    background: `linear-gradient(135deg, ${T.amberLight} 0%, ${T.amber} 100%)`,
    transform: 'translateY(-1px)',
  },
  '&:disabled': {
    background: T.border,
  }
}));

const Register = () => {
  const [data, setData] = useState({
    mobileNumber: '', name: '', email: '',
    password: '', confirmPassword: '',
  });
  const [message, setMessage] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const navigate = useNavigate();
  const registerApiUrl = process.env.REACT_APP_REGISTER_API;
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'mobileNumber') {
      const numericValue = value.replace(/\D/g, '').slice(0, 10);
      setData({ ...data, [name]: numericValue });
    } else {
      setData({ ...data, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (data.password !== data.confirmPassword) {
      setMessage('Passwords do not match.');
      setShowDialog(true);
      return;
    }
    setIsSubmitting(true);
    try {
      const mobileNumber = `+91${data.mobileNumber}`;
      const response = await axios.post(registerApiUrl, { 
        mobileNumber, name: data.name, email: data.email, password: data.password  
      });

      if (response.data.status === "User Registered") {
        localStorage.setItem('authToken', response.data.user.token);
        navigate("/DashBoard", { state: { userId: response.data.user.userId } });
      } else {
        setMessage(response.data.error || "Something went wrong.");
        setShowDialog(true);
      }
    } catch (error) {
      setMessage(error.response?.data?.error || 'Server error. Please try again.');
      setShowDialog(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = async (response) => {
    try {
      const { credential } = response;
      const userInfo = jwtDecode(credential);
      const backendResponse = await axios.post(registerApiUrl, {
        name: userInfo.name,
        email: userInfo.email,
        googleIdToken: credential,
      });
      const userData = backendResponse.data[0];
      if (userData.token) {
        localStorage.setItem('authToken', userData.token);
        navigate('/DashBoard', { state: { userId: userData.user.userId } });
      }
    } catch (error) {
      setMessage("Google sign-in failed or user already exists.");
      setShowDialog(true);
    }
  };

  return (
    <PageWrapper>
      <Fade in timeout={800}>
        <RegisterCard elevation={0}>
          <Box textAlign="center" mb={4}>
            <Typography variant="h4" sx={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, color: T.ink }}>
              Create Account
            </Typography>
            <Typography variant="body2" sx={{ color: T.inkLight, fontStyle: 'italic' }}>
              Join VIMOIR to preserve your moments
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <Stack spacing={2.5}>
              <TextField
                label="Full Name" name="name" required fullWidth
                value={data.name} onChange={handleChange}
                InputProps={{ startAdornment: <InputAdornment position="start"><PersonOutline sx={{color: T.amber}}/></InputAdornment> }}
              />

              <TextField
                label="Mobile Number" name="mobileNumber" required fullWidth
                value={data.mobileNumber} onChange={handleChange}
                placeholder="10-digit number"
                InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIphone sx={{color: T.amber}}/></InputAdornment> }}
              />

              <TextField
                label="Email (Optional)" name="email" fullWidth
                value={data.email} onChange={handleChange}
                InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlined sx={{color: T.amber}}/></InputAdornment> }}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Password" name="password" type={showPassword ? 'text' : 'password'} required fullWidth
                  value={data.password} onChange={handleChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LockOutlined sx={{color: T.amber}}/></InputAdornment>,
                    endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPassword(!showPassword)}>{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>
                  }}
                />
                <TextField
                  label="Confirm" name="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} required fullWidth
                  value={data.confirmPassword} onChange={handleChange}
                  InputProps={{
                    endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>{showConfirmPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>
                  }}
                />
              </Stack>

              <PrimaryButton type="submit" disabled={isSubmitting} sx={{ mt: 2 }}>
                {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Register Now'}
              </PrimaryButton>
            </Stack>
          </form>

          <Box my={4}>
            <Divider><Typography variant="caption" sx={{ color: T.inkLight, px: 2 }}>OR REGISTER WITH</Typography></Divider>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <GoogleOAuthProvider clientId={clientId}>
              <GoogleLogin 
                onSuccess={handleGoogleSignIn} 
                shape="pill" 
                theme="outline" 
                text="signup_with"
                width="100%"
              />
            </GoogleOAuthProvider>
          </Box>

          <Typography variant="body2" align="center" sx={{ color: T.inkMid }}>
            Already have an account?{' '}
            <Button onClick={() => navigate("/login")} sx={{ textTransform: 'none', color: T.amber, fontWeight: 700, p: 0 }}>
              Login here
            </Button>
          </Typography>
        </RegisterCard>
      </Fade>

      {/* Themed Dialog */}
      <Dialog open={showDialog} onClose={() => setShowDialog(false)} PaperProps={{ sx: { borderRadius: '20px' } }}>
        <DialogTitle sx={{ fontFamily: "'DM Serif Display', serif", color: T.amber }}>Notice</DialogTitle>
        <DialogContent><Typography>{message}</Typography></DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setShowDialog(false)} variant="contained" sx={{ borderRadius: '50px', bgcolor: T.amber }}>OK</Button>
        </DialogActions>
      </Dialog>
    </PageWrapper>
  );
};

export default Register;